export default function CTASection() {
  return (
    <section className="relative w-full py-32 bg-black overflow-hidden">
      {/* Data Flow Animation Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Flowing Data Particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: 0.6
            }}
          ></div>
        ))}
        
        {/* Glowing Orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Main Content */}
          <div className="text-center mb-16">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full mb-8">
              <i className="ri-shield-star-line text-2xl text-cyan-400"></i>
              <span className="text-cyan-400 font-bold text-lg">Build Your Data Moat</span>
            </div>

            {/* Title */}
            <h2 className="text-5xl lg:text-6xl font-bold mb-8 leading-tight" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              <span className="text-white">
                自社の現場データを、
              </span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">
                他社が模倣できない競争優位に
              </span>
            </h2>

            {/* Description */}
            <p className="text-2xl text-gray-300 leading-relaxed mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              データモート（Data Moat）を構築し、<br />
              持続可能な競争優位性を確立する。
            </p>
            <p className="text-xl text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              そのための基盤を、私たちが提供します。
            </p>
          </div>

          {/* Value Propositions */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {/* Card 1 */}
            <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 border border-cyan-500/30 rounded-2xl p-8 hover:border-cyan-400 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,212,255,0.3)]">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-database-2-line text-4xl text-cyan-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                独自データ基盤
              </h3>
              <p className="text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                あなたの現場だけの360度データを蓄積。他社には真似できない教師データ資産を構築。
              </p>
            </div>

            {/* Card 2 */}
            <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/30 rounded-2xl p-8 hover:border-orange-400 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,53,0.3)]">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-lock-line text-4xl text-orange-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                特許で保護
              </h3>
              <p className="text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                独自技術による特許で、あなたのデータ資産を法的に保護。模倣を防ぎます。
              </p>
            </div>

            {/* Card 3 */}
            <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 border border-blue-500/30 rounded-2xl p-8 hover:border-blue-400 transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-line-chart-line text-4xl text-blue-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                継続的な優位性
              </h3>
              <p className="text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                データが蓄積されるほど、AIの精度が向上。時間とともに差が開いていきます。
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button className="group relative px-16 py-7 bg-gradient-to-r from-orange-500 to-red-500 text-white text-2xl font-bold rounded-full hover:shadow-[0_0_50px_rgba(255,107,53,0.6)] hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <span className="flex items-center gap-4">
                現場データ活用について相談する
                <i className="ri-arrow-right-line text-3xl group-hover:translate-x-2 transition-transform duration-300"></i>
              </span>
            </button>

            {/* Additional Info */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 bg-gray-800/60 backdrop-blur-sm px-5 py-3 rounded-full border border-gray-700">
                <i className="ri-time-line text-lg text-cyan-400"></i>
                <span className="text-gray-300">初回相談無料</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-800/60 backdrop-blur-sm px-5 py-3 rounded-full border border-gray-700">
                <i className="ri-shield-check-line text-lg text-green-400"></i>
                <span className="text-gray-300">秘密保持契約対応</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-800/60 backdrop-blur-sm px-5 py-3 rounded-full border border-gray-700">
                <i className="ri-customer-service-2-line text-lg text-orange-400"></i>
                <span className="text-gray-300">専門チームが対応</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-100px) translateX(50px);
            opacity: 0.2;
          }
        }
      `}</style>
    </section>
  );
}
